import moment from 'moment-jalaali';
import { Article, ArticleStatus } from '../../types';
import { getPublicSiteBaseUrl } from '../../utils/publicSiteUrl';
import { getImageUrl } from '../../utils/imageUtils';

/** ساخت slug از عنوان؛ همان قاعدهٔ بک‌اند (حروف فارسی مجاز است) */
export const slugify = (title: string): string =>
  title
    .trim()
    .toLowerCase()
    .replace(/[‌\s]+/g, '-')
    .replace(/[^؀-ۿa-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export const STATUS_META: Record<ArticleStatus, { label: string; className: string }> = {
  DRAFT: { label: 'پیش‌نویس', className: 'bg-slate-100 text-slate-700 ring-slate-200' },
  SCHEDULED: { label: 'زمان‌بندی‌شده', className: 'bg-amber-50 text-amber-800 ring-amber-200' },
  PUBLISHED: { label: 'منتشر شده', className: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
};

export const articleStatus = (a: Pick<Article, 'status' | 'published' | 'publishedAt'>): ArticleStatus => {
  if (a.status) return a.status;
  if (!a.published) return 'DRAFT';
  if (a.publishedAt && new Date(a.publishedAt).getTime() > Date.now()) return 'SCHEDULED';
  return 'PUBLISHED';
};

/**
 * آدرس پایهٔ سایت برای لینک‌های وبلاگ. پنل روی admin.<دامنه> سرو می‌شود و بدون
 * REACT_APP_PUBLIC_SITE_URL آدرس خود پنل برمی‌گشت؛ زیردامنهٔ admin حذف می‌شود.
 */
export const blogSiteBaseUrl = (): string => getPublicSiteBaseUrl().replace(/^(https?:\/\/)admin\./i, '$1');

/** آدرس صفحهٔ مقاله در سایت؛ برای پیش‌نویس پارامتر preview اضافه می‌شود */
export const publicArticleUrl = (a: Pick<Article, 'slug' | 'id'>, preview = false): string => {
  const base = `${blogSiteBaseUrl()}/articles/${encodeURIComponent(a.slug)}`;
  return preview ? `${base}?preview=${encodeURIComponent(a.id)}` : base;
};

export const imagePreviewUrl = (key: string | null | undefined): string | null => getImageUrl(key || null);

export const faNumber = (n: number | null | undefined): string => (n ?? 0).toLocaleString('fa-IR');

export const faDateTime = (date: string | null | undefined): string => {
  if (!date) return '—';
  const m = moment(date);
  return m.isValid() ? m.format('jYYYY/jMM/jDD - HH:mm').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]) : '—';
};

/** «۴:۳۰» ← ۲۷۰ ثانیه و برعکس برای فیلد مدت ویدیو */
export const secondsToClock = (seconds: number | null | undefined): string => {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const clockToSeconds = (value: string): number | null => {
  const normalized = value.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).trim();
  if (!normalized) return null;
  const parts = normalized.split(':').map((p) => parseInt(p, 10));
  if (parts.some((p) => Number.isNaN(p))) return null;
  return parts.reduce((total, part) => total * 60 + part, 0);
};

export const apiErrorMessage = (err: any, fallback: string): string => {
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('، ');
  return typeof msg === 'string' && msg ? msg : fallback;
};

/** قالب‌های آماده برای بلوک‌های طراحی‌شدهٔ صفحهٔ مقاله */
export const CONTENT_SNIPPETS: Array<{ key: string; label: string; html: string }> = [
  {
    key: 'section',
    label: 'سرفصل جدید',
    html: '<h2>۱. عنوان سرفصل</h2><p>متن این بخش…</p>',
  },
  {
    key: 'quote',
    label: 'نقل‌قول برجسته',
    html: '<blockquote><p>«متن نقل‌قول»</p><cite>— منبع یا گوینده</cite></blockquote>',
  },
  {
    key: 'callout',
    label: 'کادر «نکته کلیدی»',
    html: '<div class="blog-callout"><strong>نکته کلیدی</strong><p>متن نکته…</p></div>',
  },
  {
    key: 'step',
    label: 'کارت «گام»',
    html: '<div class="blog-step"><strong>گام اول: عنوان گام</strong><p>توضیح گام…</p></div>',
  },
  {
    key: 'exercises',
    label: 'کارت‌های تمرین',
    html:
      '<div class="blog-exercises"><div class="blog-exercise"><strong>عنوان تمرین اول</strong><p>توضیح تمرین…</p></div><div class="blog-exercise"><strong>عنوان تمرین دوم</strong><p>توضیح تمرین…</p></div></div>',
  },
  {
    key: 'video',
    label: 'جای ویدیو در متن',
    html: '<p>[[video]]</p>',
  },
];
